import { PServed, useIM } from '@wui/im'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useStoreLastReadEventIds, useStoreRooms } from '@/entities/room'

import { pathNames } from '@/shared/utils/constants'

const lockedChats = ref<Set<PServed>>(new Set())

export const useMarkUnread = (pServed: PServed) => {
  const router = useRouter()
  const { getMarkedUnread } = useStoreLastReadEventIds()
  const { unMarkAsRead, deleteUnMarkAsRead } = useIM()

  const isEventsReadingLocked = computed(() => lockedChats.value.has(pServed))
  const markedUnread = computed(() => getMarkedUnread(pServed))

  const leaveOpenChat = async () => {
    const route = router.currentRoute.value
    if (route.name !== pathNames.CHAT) {
      return
    }

    const rawId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id
    if (!rawId || typeof rawId !== 'string') {
      return
    }

    let roomId: string
    try {
      roomId = atob(rawId)
    } catch {
      return
    }

    const room = useStoreRooms().findByPServed(pServed)
    if (!room || room.id !== roomId) {
      return
    }

    await router.push({ name: pathNames.MAIN })
  }

  const onMarkAsUnread = async (eventId: number) => {
    const res = await unMarkAsRead({
      to: pServed,
      eventId,
    })
    if (res) {
      lockedChats.value.add(pServed)
      const stopWatch = watch(markedUnread, (v) => {
        if (!v) {
          lockedChats.value.delete(pServed)
          stopWatch()
        }
      })

      await leaveOpenChat()
    }
  }

  const onDeleteMarkAsUnread = async (eventId: number) => {
    await deleteUnMarkAsRead({
      to: pServed,
      eventId,
    })
    lockedChats.value.delete(pServed)
  }

  const unlockMarkedUnread = () => {
    lockedChats.value.delete(pServed)
  }

  return {
    isEventsReadingLocked,
    unlockMarkedUnread,
    onMarkAsUnread,
    onDeleteMarkAsUnread,
  }
}
